import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { CalendarDays, MapPin, Package, Clock } from "lucide-react";

import { SiteHeader } from "@/components/site/site-header";
import { SiteFooter } from "@/components/site/site-footer";
import { PublicErrorState } from "@/components/site/public-error";
import { AddToCalendar } from "@/components/site/add-to-calendar";
import { inviteFromSignup } from "@/lib/calendar-invite";

import { Button } from "@/components/ui/button";
import { getManagedSignup, releaseManagedSignup } from "@/lib/signup-manage.functions";
import { formatDay, formatTime } from "./events.index";
import { reportHandledSync } from "@/lib/observability";

const signupQuery = (token: string) =>
  queryOptions({
    queryKey: ["signup", "manage", token],
    queryFn: () => getManagedSignup({ data: { token } }),
  });

export const Route = createFileRoute("/spot/$token")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(signupQuery(params.token)),
  head: () => ({
    meta: [
      { title: "Manage your spot | T1D Warriors Volunteer" },
      {
        name: "description",
        content: "View or release the volunteer spot you claimed at a T1D Warriors event.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Manage your spot | T1D Warriors Volunteer" },
      {
        property: "og:description",
        content: "View or release the volunteer spot you claimed at a T1D Warriors event.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  errorComponent: ({ error }) => (
    <PublicErrorState
      error={error}
      route="/spot"
      message="We could not load your spot just now. Refresh the page and it should come back."
    />
  ),
  component: ManageSpotPage,

});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light">
        <main
          className="mx-auto max-w-2xl px-4 pb-20 sm:px-6 sm:pb-24"
          style={{ paddingTop: "calc(var(--site-hdr, 72px) + 2.5rem)" }}
        >

          {children}
        </main>
      </div>
      <SiteFooter />

    </div>
  );
}

function ManageSpotPage() {
  const { token } = Route.useParams();
  const { data } = useSuspenseQuery(signupQuery(token));
  const release = useServerFn(releaseManagedSignup);
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const slug = data?.event_slug ?? null;

  useEffect(() => {
    track("manage_page_view", { slug });
    track("email_link_click", { slug, channel: "manage_link" });
  }, [slug]);


  if (!data) {
    return (
      <Shell>
        <h1 className="t-h1 text-ice">This link is not valid</h1>
        <p className="t-body mt-4">
          The link may have been mistyped or the signup was removed. You can always claim a new spot
          from the events board.
        </p>
        <Link to="/events" className="mt-8 inline-flex text-sm font-bold uppercase text-cyan">
          See all events
        </Link>
      </Shell>
    );
  }

  const released = data.status !== "confirmed";

  async function onRelease() {
    setBusy(true);
    try {
      const { result } = await release({
        data: { token, origin: typeof window !== "undefined" ? window.location.origin : undefined },
      });
      if (result === "not_found") {
        toast.error("We could not find that spot.");
      } else if (result === "already_released") {
        toast.message("That spot was already released.");
      } else {
        track("spot_released", { slug });
        toast.success("Your spot has been released.");
      }
      await queryClient.invalidateQueries({ queryKey: ["signup", "manage", token] });
      setConfirming(false);
    } catch (error) {
      reportHandledSync(error, { feature: "spot", action: "onRelease" });
      toast.error(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <p className="t-eyebrow text-cyan">Your spot</p>
      <h1 className="t-h1 mt-2 text-ice">
        {released ? "This spot is released" : "You are on the list"}
      </h1>
      <p className="t-body mt-4">
        {released
          ? "You are no longer signed up for this shift. It is back on the board for someone else."
          : `Thanks ${data.full_name.split(" ")[0]}. Here is what you claimed. You can release it any time from this page.`}
      </p>

      <div className="panel mt-8 p-5 sm:p-6">
        <p className="text-lg font-bold text-ice">{data.event_title}</p>
        <ul className="mt-4 space-y-3 text-sm text-foreground/85">

          <li className="flex items-start gap-3">
            <CalendarDays className="mt-0.5 size-4 shrink-0 text-cyan" />
            <span>{formatDay(data.event_date)}</span>
          </li>
          <li className="flex items-start gap-3">
            <Clock className="mt-0.5 size-4 shrink-0 text-cyan" />
            <span>
              {data.slot_title}
              {data.start_time
                ? ` - ${formatTime(data.start_time)} to ${formatTime(data.end_time)}`
                : ""}
            </span>
          </li>
          {data.slot_type === "item" ? (
            <li className="flex items-start gap-3">
              <Package className="mt-0.5 size-4 shrink-0 text-cyan" />
              <span>Bringing: {data.quantity}</span>
            </li>
          ) : null}
          {data.location_name ? (
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 size-4 shrink-0 text-cyan" />
              <span>
                {data.location_name}
                {data.address ? <span className="block text-muted-foreground">{data.address}</span> : null}
              </span>
            </li>
          ) : null}
        </ul>
      </div>

      {!released ? (
        <div className="mt-5">
          <AddToCalendar
            invite={inviteFromSignup({
              eventTitle: data.event_title,
              slotTitle: data.slot_title,
              slotType: data.slot_type,
              quantity: data.quantity,
              date: data.event_date,
              startTime: data.start_time,
              endTime: data.end_time,
              locationName: data.location_name,
              address: data.address,
              contactName: data.contact_name,
              contactEmail: data.contact_email,
              contactPhone: data.contact_phone,
              manageUrl: typeof window !== "undefined" ? window.location.href : null,
            })}
            icsHref={`/api/public/spot-calendar/${token}`}
          />
        </div>
      ) : null}

      {released ? (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Button asChild className="w-full sm:w-auto">
            <Link to="/events/$slug" params={{ slug: data.event_slug }}>
              Claim another spot
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <Link to="/events">See all events</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {confirming ? (
            <>
              <Button
                variant="destructive"
                disabled={busy}
                onClick={onRelease}
                className="w-full sm:w-auto"
              >
                {busy ? "Releasing..." : "Yes, release my spot"}
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setConfirming(false)}
                className="w-full sm:w-auto"
              >
                Keep my spot
              </Button>
            </>
          ) : (
            <>
              <Button asChild className="w-full sm:w-auto">
                <Link to="/events/$slug" params={{ slug: data.event_slug }}>
                  View the event
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirming(true)}
                className="w-full sm:w-auto"
              >
                Release my spot
              </Button>
            </>
          )}
        </div>
      )}

      <div className="mt-12 border-t border-border pt-6">
        <p className="t-body">
          Want to do more with T1D Warriors? Join the volunteer roster and get shifts, outreach
          kits, and a dashboard of your own.
        </p>
        <Link to="/apply" className="mt-3 inline-flex text-sm font-bold uppercase text-cyan">
          Apply to volunteer
        </Link>
        {data.contact_email ? (
          <p className="mt-6 text-sm text-muted-foreground">
            Questions? Email {data.contact_name ?? "us"} at{" "}
            <a className="text-cyan" href={`mailto:${data.contact_email}`}>
              {data.contact_email}
            </a>
            {data.contact_phone ? (
              <>
                {" "}
                or call{" "}
                <a className="text-cyan" href={`tel:${data.contact_phone}`}>
                  {data.contact_phone}
                </a>
              </>
            ) : null}
            .
          </p>
        ) : null}
      </div>
    </Shell>
  );
}
